<script setup lang="ts">
// 1:1 port of the styleguide `kgr-modal-header`: headline block plus close button, with the host
// classes Angular sets via @HostBinding. In Vue the markup had been inlined in every modal, so the
// copies had already started to drift (aria-label vs aria-hidden, `.headline` class present or not).
import KgrIcon from './KgrIcon.vue';

withDefaults(
    defineProps<{
        headline?: string
        subheadline?: string
        accent?: boolean
        small?: boolean
        showShadow?: boolean
        noClose?: boolean
        /** Angular sets `aria-hidden` on the button; call sites that label it pass this instead. */
        closeLabel?: string
    }>(),
    { accent: true, small: false, showShadow: false, noClose: false },
);

const emit = defineEmits<{ close: [] }>();
</script>

<template>
    <div
        class="kgr-modal__header kgr-region"
        :class="{ 'kgr-region--accent': accent, 'kgr-region--small': small, 'has-shadow': showShadow }"
    >
        <slot name="headerStart"></slot>
        <div
            v-if="headline || subheadline"
            class="kgr-modal__headerContent"
        >
            <h1
                v-if="headline"
                class="headline kgr-headline kgr-headline--h3"
            >
                {{ headline }}
            </h1>
            <h2
                v-if="subheadline"
                class="kgr-headline kgr-headline--h5"
            >
                {{ subheadline }}
            </h2>
        </div>
        <button
            v-if="!noClose"
            type="button"
            class="closeIcon kgr-modal__headerClose kgr-clickable"
            :aria-label="closeLabel"
            :aria-hidden="closeLabel ? undefined : 'true'"
            @click="emit('close')"
        >
            <KgrIcon name="clear" />
        </button>
        <slot></slot>
    </div>
</template>

<style scoped>
.has-shadow {
    box-shadow: 0 4px 5px -2px rgba(0, 0, 0, 0.2);
}

.headline {
    max-width: calc(100vw - 2 * var(--kgr-region-padding));
    overflow-wrap: break-word;
}

.kgr-modal__header:has(.closeIcon) .headline {
    max-width: calc(100vw - 2 * var(--kgr-region-padding) - var(--kgr-icon-font-size) - var(--kgr-default-margin));
    overflow-wrap: break-word;
}
</style>
